import React, { useState, useEffect } from 'react';
import { 
  Card, Row, Col, Statistic, Spin, Select, DatePicker, Radio, Space, 
  Button, Divider, Alert, Empty, Tabs, Typography
} from 'antd';
import { Bar, Pie, Doughnut } from 'react-chartjs-2';
import {
  FormOutlined,
  FileTextOutlined,
  ReadOutlined,
  ClockCircleOutlined,
  ExperimentOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import 'chart.js/auto';
import { generateExamService } from '../../services/generateExamService';
import type { 
  ExamStatistics, 
  ExamStatsQuery,
  QuestionByTimeStats,
  ExamBySubjectStats 
} from '../../../../shared/types/database';

const { Option } = Select;
const { TabPane } = Tabs;
const { Title, Text } = Typography;

type PeriodType = 'month' | 'quarter' | 'year';

interface ExamStatisticsPageState {
  loading: boolean;
  periodType: PeriodType;
  year: number;
  month: number;
  quarter: number;
  statsData: ExamStatistics | null;
  error: string | null;
  hasPermission: boolean;
}

const ExamStatisticsPage: React.FC = () => {
  const [state, setState] = useState<ExamStatisticsPageState>({
    loading: false,
    periodType: 'month',
    year: dayjs().year(),
    month: dayjs().month() + 1,
    quarter: Math.ceil((dayjs().month() + 1) / 3),
    statsData: null,
    error: null,
    hasPermission: true // Mặc định là true, backend sẽ handle permission check
  });

  // Helper function để update state
  const updateState = (updates: Partial<ExamStatisticsPageState>) => {
    setState(prev => ({ ...prev, ...updates }));
  };

  // Kiểm tra quyền truy cập - Đơn giản hóa vì backend đã handle permission check
  useEffect(() => {
    const checkPermission = (): void => {
      // Lấy thông tin user để log debug
      const oldUserInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
      const newUserInfo = JSON.parse(localStorage.getItem('quanly_truonghoc_user') || '{}');
      const userInfo = newUserInfo?.UserId ? newUserInfo : oldUserInfo;
      
      console.log('� User info:', userInfo);
      
      // Mặc định cho phép truy cập, backend sẽ kiểm tra quyền
      updateState({ hasPermission: true });
    };
    
    checkPermission();
  }, []);

  // Tạo mảng năm từ 2020 đến năm hiện tại
  const years: number[] = [];
  for (let y = 2020; y <= dayjs().year(); y++) {
    years.push(y);
  }

  // Load dữ liệu khi component được mount và khi các bộ lọc thay đổi
  useEffect(() => {
    fetchStatistics();
  }, [state.periodType, state.year, state.month, state.quarter]);

  // Lấy dữ liệu thống kê từ API
  const fetchStatistics = async (): Promise<void> => {
    try {
      updateState({ 
        loading: true,
        error: null 
      });
      
      // Xây dựng tham số truy vấn
      const params: ExamStatsQuery = { 
        period: state.periodType, 
        year: state.year 
      };
      
      if (state.periodType === 'month') {
        params.month = state.month;
      } else if (state.periodType === 'quarter') {
        params.quarter = state.quarter;
      }
      
      const response = await generateExamService.getExamStatistics(params);
      console.log('API response:', response);
      updateState({ 
        statsData: response,
        loading: false 
      });
    } catch (error: any) {
      console.error('Lỗi khi lấy dữ liệu thống kê:', error);
      updateState({
        error: error.response?.data?.error || 'Không thể lấy dữ liệu thống kê',
        statsData: null,
        loading: false
      });
    }
  };

  // Xử lý thay đổi loại kỳ thống kê
  const handlePeriodChange = (e: any): void => {
    updateState({ periodType: e.target.value });
  };

  // Xử lý thay đổi năm
  const handleYearChange = (value: number): void => {
    updateState({ year: value });
  };

  // Xử lý thay đổi tháng
  const handleMonthChange = (value: number): void => {
    updateState({ month: value });
  };

  // Xử lý thay đổi quý
  const handleQuarterChange = (value: number): void => {
    updateState({ quarter: value });
  };

  // Format tiêu đề theo loại kỳ thống kê
  const getPeriodTitle = (): string => {
    if (state.periodType === 'month') {
      return `Tháng ${state.month}/${state.year}`;
    } else if (state.periodType === 'quarter') {
      return `Quý ${state.quarter}/${state.year}`;
    } else {
      return `Năm ${state.year}`;
    }
  };

  // Render biểu đồ phân bố câu hỏi theo thời gian
  const renderQuestionByTimeChart = (): JSX.Element => {
    if (!state.statsData || !state.statsData.questionByTime || state.statsData.questionByTime.length === 0) {
      return <Empty description="Không có dữ liệu" />;
    }

    const data = {
      labels: state.statsData.questionByTime.map((item: QuestionByTimeStats) => `${item.ThoiGianDuKien} phút`),
      datasets: [
        {
          label: 'Số lượng câu hỏi',
          data: state.statsData.questionByTime.map((item: QuestionByTimeStats) => item.Count),
          backgroundColor: [
            '#1890ff', '#36cbcb', '#4ecb73', '#fbd144', '#f2637b',
            '#975fe4', '#6c8dfa', '#4adbc3', '#3fca3f', '#ffaa15'
          ],
          borderWidth: 1,
        },
      ],
    };

    const options = {
      responsive: true,
      plugins: {
        legend: {
          position: 'right' as const,
        },
        title: {
          display: true,
          text: 'Phân bố câu hỏi theo thời gian làm bài',
        },
      },
    };

    return <Doughnut data={data} options={options} />;
  };

  // Render biểu đồ đề thi theo môn học
  const renderExamBySubjectChart = (): JSX.Element => {
    if (!state.statsData || !state.statsData.examBySubject || state.statsData.examBySubject.length === 0) {
      return <Empty description="Không có dữ liệu" />;
    }

    // Giới hạn hiển thị 10 môn học nhiều đề thi nhất
    const topSubjects = state.statsData.examBySubject.slice(0, 10);

    const data = {
      labels: topSubjects.map((item: ExamBySubjectStats) => item.TenMonHoc),
      datasets: [
        {
          label: 'Số lượng đề thi',
          data: topSubjects.map((item: ExamBySubjectStats) => item.Count),
          backgroundColor: '#1890ff',
        },
      ],
    };

    const options = {
      responsive: true,
      indexAxis: 'y' as const,
      plugins: {
        legend: {
          display: false,
        },
        title: {
          display: true,
          text: 'Số lượng đề thi theo môn học',
        },
      },
      scales: {
        x: {
          beginAtZero: true,
          title: {
            display: true,
            text: 'Số lượng đề thi'
          }
        },
        y: {
          title: {
            display: true,
            text: 'Môn học'
          }
        }
      }
    };

    return <Bar data={data} options={options} />;
  };

  return (
    <div style={{ padding: 24 }}>
      <Card title="Thống kê module Khảo Thí" className="stats-card">
        <div style={{ marginBottom: 20 }}>
          <Space size="large" wrap>
            <Radio.Group 
              value={state.periodType} 
              onChange={handlePeriodChange}
              buttonStyle="solid"
            >
              <Radio.Button value="month">Tháng</Radio.Button>
              <Radio.Button value="quarter">Quý</Radio.Button>
              <Radio.Button value="year">Năm</Radio.Button>
            </Radio.Group>

                <Select
                  value={state.year}
                  onChange={handleYearChange}
                  style={{ width: 100 }}
                >
                  {years.map(y => (
                    <Option key={y} value={y}>{y}</Option>
                  ))}
                </Select>

                {state.periodType === 'month' && (
                  <Select
                    value={state.month}
                    onChange={handleMonthChange}
                    style={{ width: 100 }}
                  >
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
                      <Option key={m} value={m}>{m}</Option>
                    ))}
                  </Select>
                )}

                {state.periodType === 'quarter' && (
                  <Select
                    value={state.quarter}
                    onChange={handleQuarterChange}
                    style={{ width: 100 }}
                  >
                    {[1, 2, 3, 4].map(q => (
                      <Option key={q} value={q}>{q}</Option>
                    ))}
                  </Select>
                )}

                <Button type="primary" onClick={fetchStatistics} loading={state.loading}>
                  Xem thống kê
                </Button>
              </Space>
            </div>

            {state.error && (
              <Alert
                message="Lỗi"
                description={state.error}
                type="error"
                showIcon
                style={{ marginBottom: 20 }}
              />
            )}

            {state.loading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: 50 }}>
                <Spin size="large" />
              </div>
            ) : state.statsData ? (
              <>
                <Title level={2}>{getPeriodTitle()}</Title>
                <Row gutter={24} style={{ marginBottom: 32 }}>
                  <Col xs={24} sm={12} md={8}>
                    <Card style={{ background: 'linear-gradient(135deg, #fffbe6 0%, #ffe58f 100%)', border: 'none', boxShadow: '0 4px 18px 0 rgba(251,191,36,0.10)' }}>
                      <Statistic 
                        title="Câu hỏi ngân hàng đề" 
                        value={state.statsData.totalQuestions} 
                        prefix={<FormOutlined style={{ color: '#d97706' }} />} 
                        valueStyle={{ color: '#d97706', fontWeight: 600 }} 
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <Card style={{ background: 'linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%)', border: 'none', boxShadow: '0 4px 18px 0 rgba(168,85,247,0.10)' }}>
                      <Statistic 
                        title="Đề thi" 
                        value={state.statsData.totalExams} 
                        prefix={<FileTextOutlined style={{ color: '#9333ea' }} />} 
                        valueStyle={{ color: '#9333ea', fontWeight: 600 }} 
                      />
                    </Card>
                  </Col>
                  <Col xs={24} sm={12} md={8}>
                    <Card style={{ background: 'linear-gradient(135deg, #e6fffa 0%, #b2f5ea 100%)', border: 'none', boxShadow: '0 4px 18px 0 rgba(34,197,94,0.10)' }}>
                      <Statistic 
                        title="Môn thi" 
                        value={state.statsData.totalSubjects} 
                        prefix={<ReadOutlined style={{ color: '#059669' }} />} 
                        valueStyle={{ color: '#059669', fontWeight: 600 }} 
                      />
                    </Card>
                  </Col>
                </Row>

                <Tabs defaultActiveKey="1">
                  <TabPane tab="Phân bố câu hỏi theo thời gian" key="1">
                    <div style={{ height: 400 }}>
                      {renderQuestionByTimeChart()}
                    </div>
                  </TabPane>
                  <TabPane tab="Đề thi theo môn học" key="2">
                    <div style={{ height: 400 }}>
                      {renderExamBySubjectChart()}
                    </div>
                  </TabPane>
                </Tabs>
              </>
            ) : (
              <Empty description="Không có dữ liệu thống kê" />
            )}
        
      </Card>
    </div>
  );
};

export default ExamStatisticsPage;
